import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '@/components/ui/navigation-menu';
import { Search, LogIn, LogOut, Eye, EyeOff, Shield, Menu, UserPlus, Info, Mail, ChevronRight, X, Lock } from 'lucide-react';
import ContactModal from '@/components/ContactModal';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
interface HeaderProps {
  onSearchClick?: () => void;
  isAdminMode?: boolean;
  onAdminModeChange?: (isAdminMode: boolean) => void;
  showSecurityDashboard?: boolean;
  onSecurityDashboardChange?: (show: boolean) => void;
  showSearch?: boolean;
  showAuth?: boolean;
  showAdminControls?: boolean;
  transparentOnMobile?: boolean;
  showNavigation?: boolean;
  onGetStarted?: () => void;
}
const Header = ({
  onSearchClick,
  isAdminMode,
  onAdminModeChange,
  showSecurityDashboard,
  onSecurityDashboardChange,
  showSearch = true,
  showAuth = true,
  showAdminControls = true,
  transparentOnMobile = false,
  showNavigation = true,
  onGetStarted
}: HeaderProps) => {
  const {
    user,
    signOut
  } = useAuth();
  const {
    isAdmin
  } = useAdminAuth();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const isActive = (path: string) => location.pathname === path;
  
  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  const toggleAdminMode = () => {
    if (onAdminModeChange) {
      onAdminModeChange(!isAdminMode);
      toast({
        title: isAdminMode ? "Admin Mode Disabled" : "Admin Mode Enabled",
        description: isAdminMode ? "Switched back to user view" : "Admin tools are now visible"
      });
    }
  };

  const toggleSecurityDashboard = () => {
    if (onSecurityDashboardChange) {
      onSecurityDashboardChange(!showSecurityDashboard);
    }
  };
  const isHomePage = location.pathname === '/';
  const useTransparentStyle = transparentOnMobile || isHomePage;
  
  return <header 
      className={cn(
        "z-50 w-full",
        useTransparentStyle 
          ? "absolute top-0 left-0 right-0 bg-transparent md:sticky md:bg-background md:border-b md:border-border" 
          : "bg-background border-b border-border sticky top-0"
      )}
      role="banner"
    >
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className={cn(
        "max-w-[1280px] mx-auto",
        useTransparentStyle 
          ? "px-4 md:px-8 lg:px-12 py-1 md:py-3 lg:py-4"
          : "px-4 md:px-8 lg:px-12 py-4 md:py-3 lg:py-4"
      )}>
        <div className="md:hidden flex items-center justify-between">
          <Button
            variant="ghost"
            className={cn(
              "text-base font-display font-semibold tracking-tight p-2 hover:bg-transparent",
              useTransparentStyle ? "text-white hover:text-white" : "text-foreground"
            )}
            onClick={() => navigate('/')}
          >
            SINK BIKE RUN
          </Button>

          <div className="flex items-center gap-2">
            {showSearch && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "transition-colors",
                  useTransparentStyle 
                    ? "text-white hover:bg-white/10" 
                    : ""
                )}
                onClick={onSearchClick}
                aria-label="Search checklists"
              >
                <Search className="h-4 w-4 md:h-6 md:w-6" />
              </Button>
            )}

            {showNavigation && (
            <Sheet open={mobileMenuOpen} onOpenChange={(open) => { if (open) (document.activeElement as HTMLElement)?.blur(); setMobileMenuOpen(open); }}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "transition-colors",
                    useTransparentStyle 
                      ? "text-white hover:bg-white/10" 
                      : ""
                  )}
                  aria-label="Open Menu"
                >
                  <Menu className="h-4 w-4 md:h-6 md:w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] bg-background p-0" overlayClassName="bg-transparent" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
                <div className="flex flex-col h-full">
                  <nav className="flex flex-col px-8 py-4 gap-1 mt-8">
                    <button
                      onClick={() => handleNavigation('/')}
                      className={cn(
                        "flex items-center justify-between py-3 transition-colors text-left group",
                        isActive('/') 
                          ? "text-primary font-medium" 
                          : "text-foreground hover:text-foreground/80"
                      )}
                    >
                      <span className="text-sm font-body">Home</span>
                      <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>
                    
                    <button
                      onClick={() => handleNavigation('/about')}
                      className={cn(
                        "flex items-center justify-between py-3 transition-colors text-left group",
                        isActive('/about') 
                          ? "text-primary font-medium" 
                          : "text-foreground hover:text-foreground/80"
                      )}
                    >
                      <span className="text-sm font-body">About</span>
                      <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>
                    
                    {onGetStarted && (
                      <button
                        onClick={() => {
                          onGetStarted();
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                      >
                        <span className="text-sm font-body">Getting Started</span>
                        <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setIsContactModalOpen(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                    >
                      <span className="text-sm font-body">Contact</span>
                      <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>

                    {showAuth && <div className="h-px bg-border my-4" />}
                    
                    {showAuth && (
                      <>
                        {!user ? (
                          <button
                            onClick={() => handleNavigation('/auth?tab=signin')}
                            className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                          >
                            <span className="text-sm font-body">Account</span>
                            <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ) : (
                          <button
                            onClick={async () => {
                              await signOut();
                              setMobileMenuOpen(false);
                              toast({
                                title: "Signed out successfully",
                                description: "You have been logged out of your account"
                              });
                            }}
                            className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left"
                          >
                            <span className="text-sm font-body">Sign Out</span>
                          </button>
                        )}
                      </>
                    )}

                    {showAdminControls && isAdmin && (
                      <>
                        <div className="h-px bg-border my-4" />
                        <button
                          onClick={() => {
                            toggleAdminMode();
                            setMobileMenuOpen(false);
                          }}
                          className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            <span className="text-sm font-body">{isAdminMode ? "User View" : "Admin Mode"}</span>
                          </div>
                          <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                        </button>
                        <button
                          onClick={() => {
                            toggleSecurityDashboard();
                            setMobileMenuOpen(false);
                          }}
                          className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            <span className="text-sm font-body">Security</span>
                          </div>
                          <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </>
                    )}
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
            )}
          </div>
        </div>

        <ContactModal 
          isOpen={isContactModalOpen} 
          onClose={() => setIsContactModalOpen(false)} 
        />

        <div className="hidden md:flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-left">
              <h1 className="text-3xl lg:text-4xl font-display font-semibold tracking-tight leading-none text-foreground cursor-pointer hover:opacity-70 transition-opacity" onClick={() => navigate('/')}>
                SINK BIKE RUN
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {showSearch && (
              <Button
                variant="ghost"
                size="icon"
                className="transition-colors"
                onClick={onSearchClick}
                aria-label="Search checklists"
              >
                <Search className="h-4 w-4 md:h-6 md:w-6" />
              </Button>
            )}

            {showNavigation && (
            <Sheet open={mobileMenuOpen} onOpenChange={(open) => { if (open) (document.activeElement as HTMLElement)?.blur(); setMobileMenuOpen(open); }}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="transition-colors"
                  aria-label="Open Menu"
                >
                  <Menu className="h-4 w-4 md:h-6 md:w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] bg-background p-0" overlayClassName="bg-transparent" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
                <div className="flex flex-col h-full">
                  <nav className="flex flex-col px-8 py-4 gap-1 mt-8">
                    <button
                      onClick={() => handleNavigation('/')}
                      className={cn(
                        "flex items-center justify-between py-3 transition-colors text-left group",
                        isActive('/') 
                          ? "text-primary font-medium" 
                          : "text-foreground hover:text-foreground/80"
                      )}
                    >
                      <span className="text-sm">Home</span>
                      <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>
                    
                    <button
                      onClick={() => handleNavigation('/about')}
                      className={cn(
                        "flex items-center justify-between py-3 transition-colors text-left group",
                        isActive('/about') 
                          ? "text-primary font-medium" 
                          : "text-foreground hover:text-foreground/80"
                      )}
                    >
                      <span className="text-sm">About</span>
                      <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>
                    
                    {onGetStarted && (
                      <button
                        onClick={() => {
                          onGetStarted();
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                      >
                        <span className="text-sm font-normal">Getting Started</span>
                        <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        setIsContactModalOpen(true);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                    >
                      <span className="text-sm font-normal">Contact</span>
                      <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </button>

                    {showAuth && <div className="h-px bg-border my-4" />}
                    
                    {showAuth && (
                      <>
                        {!user ? (
                          <button
                            onClick={() => handleNavigation('/auth?tab=signin')}
                            className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                          >
                            <span className="text-sm font-normal">Account</span>
                            <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ) : (
                          <button
                            onClick={async () => {
                              await signOut();
                              setMobileMenuOpen(false);
                              toast({
                                title: "Signed out successfully",
                                description: "You have been logged out of your account"
                              });
                            }}
                            className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left"
                          >
                            <span className="text-sm font-normal">Sign Out</span>
                          </button>
                        )}
                      </>
                    )}

                    {showAdminControls && isAdmin && (
                      <>
                        <div className="h-px bg-border my-4" />
                        <button
                          onClick={() => {
                            toggleAdminMode();
                            setMobileMenuOpen(false);
                          }}
                          className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            <span className="text-sm font-normal">{isAdminMode ? "User View" : "Admin Mode"}</span>
                          </div>
                          <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                        </button>
                        <button
                          onClick={() => {
                            toggleSecurityDashboard();
                            setMobileMenuOpen(false);
                          }}
                          className="flex items-center justify-between py-3 text-foreground hover:text-foreground/80 transition-colors text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            <span className="text-sm font-normal">Security</span>
                          </div>
                          <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </>
                    )}
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
            )}
          </div>
        </div>
      </div>
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </header>;
};
export default Header;
